import React, { useState, useEffect } from 'react';
import { Button, Image, Spin, message, Upload, Space } from 'antd';
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { getUploadSignature, deleteImageFromCloudinary } from '../apiCalls.js';


/**
 * Extracts the Cloudinary public_id (folder path + filename, no version or extension)
 * from a delivery URL. Returns null for anything that is not a Cloudinary upload URL.
 * URL format: .../upload/v{version}/{folder}/{filename}.{ext}
 */
export const extractCloudinaryPublicId = (url) => {
    if (typeof url !== 'string') return null;
    const uploadIndex = url.indexOf('/upload/');
    if (uploadIndex === -1) return null;
    const afterUpload = url.substring(uploadIndex + '/upload/'.length).split(/[?#]/)[0];
    const withoutTransforms = afterUpload.replace(/^(?:[a-z_]+_[^/]+,?)+\//, '');
    const withoutVersion = withoutTransforms.replace(/^v\d+\//, '');
    const publicId = withoutVersion.replace(/\.[^/.]+$/, '');
    return publicId ? decodeURIComponent(publicId) : null;
};

/**
 * ImageUpload Component
 * Handles secure image uploads to Cloudinary using signed requests
 * 
 * Props:
 * - imageUrl: Current image URL
 * - onImageUpload: Callback function when image successfully uploads
 * - onImageDelete: Callback function when image is deleted
 * - maxSize: Maximum file size in MB (default: 5)
 * - folder: Cloudinary folder for organizing uploads
 */
const ImageUpload = ({
    imageUrl,
    propertyID,
    onImageUpload,
    onImageDelete,
    maxSize = 5,
    folder = 'uk-solar'
}) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(imageUrl);

    // Sync previewUrl with imageUrl prop whenever it changes
    useEffect(() => {
        setPreviewUrl(imageUrl);
    }, [imageUrl]);

    const handleUpload = async (file) => {
        // Validation
        const maxBytes = maxSize * 1024 * 1024;
        if (file.size > maxBytes) {
            message.error(t('imageUpload.error.tooLarge', { maxSize }));
            return false;
        }

        const validFormats = ['image/jpeg', 'image/png', 'image/webp'];
        if (!validFormats.includes(file.type)) {
            message.error(t('imageUpload.error.invalidType'));
            return false;
        }

        setLoading(true);

        try {
            // Step 1: Get signed upload credentials from backend
            const signatureResponse = await getUploadSignature(propertyID);
            const { timestamp, signature, cloud_name, api_key, folder: uploadFolder } = signatureResponse;

            // Step 2: Create FormData for Cloudinary upload
            const formData = new FormData();
            formData.append('file', file);
            formData.append('timestamp', timestamp);
            formData.append('signature', signature);
            formData.append('api_key', api_key);
            formData.append('folder', uploadFolder);

            // Step 3: Upload directly to Cloudinary
            const response = await axios.post(
                `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            const uploadedUrl = response.data.secure_url;
            const publicId = response.data.public_id;

            // Step 4: Update preview and call callback
            setPreviewUrl(uploadedUrl);
            message.success(t('imageUpload.success.uploaded'));

            if (onImageUpload) {
                onImageUpload({
                    url: uploadedUrl,
                    publicId: publicId,
                    size: response.data.bytes,
                    width: response.data.width,
                    height: response.data.height,
                });
            }

            return true;
        } catch (error) {
            console.error('Upload error:', error);
            message.error(t('imageUpload.error.upload'));
            return false;
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!previewUrl) {
            message.warning(t('imageUpload.error.noImage'));
            return;
        }

        setLoading(true);

        try {
            // Only Cloudinary-hosted assets can be deleted remotely. Anything else (legacy
            // URLs, data URIs) is simply detached from the property.
            const publicId = extractCloudinaryPublicId(previewUrl);
            if (publicId) {
                await deleteImageFromCloudinary(propertyID, publicId);
            }

            setPreviewUrl(null);
            message.success(t('imageUpload.success.deleted'));

            if (onImageDelete) {
                onImageDelete();
            }
        } catch (error) {
            console.error('Delete error:', error);
            message.error(t('imageUpload.error.delete'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ padding: '16px', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
            <Spin spinning={loading}>
                <Space direction="vertical" style={{ width: '100%' }}>
                    {previewUrl && (
                        <div>
                            <Image
                                src={previewUrl}
                                alt={t('imageUpload.uploadedImageAlt')}
                                style={{ maxHeight: '300px', maxWidth: '100%' }}
                                preview
                            />
                        </div>
                    )}

                    <Space>
                        <Upload
                            beforeUpload={handleUpload}
                            maxCount={1}
                            showUploadList={false}
                        >
                            <Button
                                icon={<UploadOutlined />}
                                loading={loading}
                                disabled={loading}
                            >
                                {previewUrl ? t('imageUpload.replace') : t('imageUpload.upload')}
                            </Button>
                        </Upload>

                        {previewUrl && (
                            <Button
                                danger
                                icon={<DeleteOutlined />}
                                onClick={handleDelete}
                                loading={loading}
                                disabled={loading}
                            >
                                {t('imageUpload.delete')}
                            </Button>
                        )}
                    </Space>
                </Space>
            </Spin>
        </div>
    );
};

export default ImageUpload;
